import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "ghost" | "quiet" | "danger" | "danger-solid";
export type ButtonSize = "sm" | "md" | "lg";
export type ArrowDir = "right" | "down";

interface Common {
  variant?: ButtonVariant;
  /** Domyślnie `lg` — tak jak w komponencie `Button` design systemu. */
  size?: ButtonSize;
  block?: boolean;
  /** `true` = strzałka w prawo; można też podać kierunek. */
  arrow?: boolean | ArrowDir;
  children?: ReactNode;
}

type ButtonProps = Common & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;
type LinkProps = Common & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children"> & { href: string };

function classes(v: ButtonVariant, s: ButtonSize, block?: boolean, extra?: string): string {
  return ["ui-btn", `ui-btn--${v}`, `ui-btn--${s}`, block ? "ui-btn--block" : "", extra ?? ""]
    .filter(Boolean)
    .join(" ");
}

/** Strzałka CTA — ta sama geometria co w design systemie (18×18, grubość 2.4). */
function Arrow({ dir }: { dir: ArrowDir }) {
  const d = dir === "down" ? "M12 5v14M6 13l6 6 6-6" : "M5 12h14M13 6l6 6-6 6";
  return (
    <svg
      className={`ui-btn-arrow-${dir}`}
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <path d={d} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const arrowDir = (arrow: Common["arrow"]): ArrowDir | null =>
  arrow === true ? "right" : arrow === false || arrow == null ? null : arrow;

export function Button({
  variant = "primary",
  size = "lg",
  block,
  arrow,
  children,
  className,
  type = "button",
  ...rest
}: ButtonProps) {
  const dir = arrowDir(arrow);
  return (
    <button type={type} className={classes(variant, size, block, className)} {...rest}>
      {children}
      {dir ? <Arrow dir={dir} /> : null}
    </button>
  );
}

export function ButtonLink({
  variant = "primary",
  size = "lg",
  block,
  arrow,
  children,
  className,
  ...rest
}: LinkProps) {
  const dir = arrowDir(arrow);
  return (
    <a className={classes(variant, size, block, className)} {...rest}>
      {children}
      {dir ? <Arrow dir={dir} /> : null}
    </a>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "md" | "lg";
}

/** Kwadratowy przycisk nawigacyjny („‹", „›") z paska kalendarza i paginacji. */
export function IconButton({ size = "md", className, type = "button", ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      className={["ui-iconbtn", size === "lg" ? "ui-iconbtn--lg" : "", className ?? ""].filter(Boolean).join(" ")}
      {...rest}
    />
  );
}
